/**
 * MediFlow Pflege — Motion System
 * ─────────────────────────────────
 * Teal-branded animation system for care app.
 * Same architecture as practice-web, adapted for care context.
 */

export const MOTION = {
    durationMs: {
        micro: 120,
        component: 180,
        layout: 260,
        emphasis: 360,
        hero: 600,
        cinematic: 900,
        toastExit: 140,
    },
    easing: {
        standard: [0.2, 0, 0, 1] as const,
        emphasis: [0.22, 1, 0.36, 1] as const,
        decelerate: [0, 0, 0.2, 1] as const,
        accelerate: [0.4, 0, 1, 1] as const,
        sharp: [0.4, 0, 0.6, 1] as const,
        bounce: [0.34, 1.56, 0.64, 1] as const,
    },
} as const;

export const MOTION_SECONDS = {
    micro: MOTION.durationMs.micro / 1000,
    component: MOTION.durationMs.component / 1000,
    layout: MOTION.durationMs.layout / 1000,
    emphasis: MOTION.durationMs.emphasis / 1000,
    hero: MOTION.durationMs.hero / 1000,
    cinematic: MOTION.durationMs.cinematic / 1000,
    toastExit: MOTION.durationMs.toastExit / 1000,
} as const;

export const FRAMER_TRANSITIONS = {
    micro: { duration: MOTION_SECONDS.micro, ease: MOTION.easing.standard },
    component: { duration: MOTION_SECONDS.component, ease: MOTION.easing.standard },
    layout: { duration: MOTION_SECONDS.layout, ease: MOTION.easing.standard },
    emphasis: { duration: MOTION_SECONDS.emphasis, ease: MOTION.easing.emphasis },
    hero: { duration: MOTION_SECONDS.hero, ease: MOTION.easing.emphasis },
    cinematic: { duration: MOTION_SECONDS.cinematic, ease: MOTION.easing.decelerate },
    toastExit: { duration: MOTION_SECONDS.toastExit, ease: MOTION.easing.standard },
} as const;

export const SPRING = {
    snappy: { type: 'spring' as const, stiffness: 500, damping: 30, mass: 0.5 },
    smooth: { type: 'spring' as const, stiffness: 300, damping: 30, mass: 0.8 },
    bouncy: { type: 'spring' as const, stiffness: 400, damping: 15, mass: 0.6 },
    gentle: { type: 'spring' as const, stiffness: 120, damping: 20, mass: 1 },
    heavy: { type: 'spring' as const, stiffness: 200, damping: 40, mass: 1.2 },
    pill: { type: 'spring' as const, stiffness: 420, damping: 34, mass: 0.65 },
    cardHover: { type: 'spring' as const, stiffness: 260, damping: 25, mass: 0.7 },
    layoutShift: { type: 'spring' as const, stiffness: 350, damping: 35, mass: 0.9 },
    modal: { type: 'spring' as const, stiffness: 280, damping: 28, mass: 0.85 },
} as const;

export const STAGGER = {
    fast: { staggerChildren: 0.03, delayChildren: 0 },
    default: { staggerChildren: 0.06, delayChildren: 0.05 },
    slow: { staggerChildren: 0.1, delayChildren: 0.1 },
    dramatic: { staggerChildren: 0.15, delayChildren: 0.2 },
    cascade: { staggerChildren: 0.08, delayChildren: 0.15 },
} as const;

export const VARIANTS = {
    fadeUp: {
        hidden: { opacity: 0, y: 24, filter: 'blur(4px)' },
        visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { ...FRAMER_TRANSITIONS.emphasis } },
    },
    fadeUpSubtle: {
        hidden: { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { ...FRAMER_TRANSITIONS.layout } },
    },
    fadeIn: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { ...FRAMER_TRANSITIONS.component } },
    },
    scaleIn: {
        hidden: { opacity: 0, scale: 0.92 },
        visible: { opacity: 1, scale: 1, transition: SPRING.smooth },
    },
    slideLeft: {
        hidden: { opacity: 0, x: -32 },
        visible: { opacity: 1, x: 0, transition: { ...FRAMER_TRANSITIONS.emphasis } },
    },
    slideRight: {
        hidden: { opacity: 0, x: 32 },
        visible: { opacity: 1, x: 0, transition: { ...FRAMER_TRANSITIONS.emphasis } },
    },
    staggerContainer: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: STAGGER.default },
    },
    drawer: {
        hidden: { x: '100%', opacity: 0.5 },
        visible: { x: 0, opacity: 1, transition: SPRING.modal },
        exit: { x: '100%', opacity: 0, transition: { duration: 0.2, ease: MOTION.easing.accelerate } },
    },
    backdrop: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
    },
    badgeBounce: {
        hidden: { scale: 0, opacity: 0 },
        visible: { scale: 1, opacity: 1, transition: SPRING.bouncy },
    },
    cardLift: {
        rest: { y: 0, scale: 1, boxShadow: '0 2px 8px -2px rgba(15,23,42,0.05)' },
        hover: { y: -4, scale: 1.01, boxShadow: '0 16px 32px -8px rgba(15,23,42,0.10)', transition: SPRING.cardHover },
        tap: { scale: 0.98, y: -1, transition: { duration: 0.1 } },
    },
    float: {
        animate: { y: [0, -6, 0], transition: { duration: 5, repeat: Infinity, ease: 'easeInOut' } },
    },
    pulseGlow: {
        animate: { scale: [1, 1.04, 1], opacity: [0.7, 1, 0.7], transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
    },
} as const;

export const SCROLL_TRIGGER = {
    section: { once: true, amount: 0.2 as const, margin: '-50px' as const },
    card: { once: true, amount: 0.15 as const },
    hero: { once: true, amount: 0.1 as const },
    counter: { once: true, amount: 0.3 as const },
} as const;

export const MICRO = {
    buttonPress: { whileTap: { scale: 0.97 }, transition: { duration: 0.1 } },
    cardHover: { whileHover: { y: -4, scale: 1.01 }, whileTap: { scale: 0.98 }, transition: SPRING.cardHover },
    iconSpin: { whileHover: { rotate: 15 }, transition: SPRING.snappy },
    linkHover: { whileHover: { x: 4 }, transition: SPRING.snappy },
    scaleHover: { whileHover: { scale: 1.03 }, whileTap: { scale: 0.97 }, transition: SPRING.gentle },
    focusRing: { whileFocus: { boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.2)' }, transition: { duration: 0.15 } },
} as const;

export function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function safeTransition(transition: Record<string, unknown>): Record<string, unknown> {
    if (prefersReducedMotion()) return { duration: 0 };
    return transition;
}

export function staggerDelay(index: number, base: number = 0.06): number {
    return index * base;
}

export function scrollAnimationProps(
    variant: keyof typeof VARIANTS = 'fadeUp',
    trigger: keyof typeof SCROLL_TRIGGER = 'section',
) {
    return {
        variants: VARIANTS[variant],
        initial: 'hidden',
        whileInView: 'visible',
        viewport: SCROLL_TRIGGER[trigger],
    };
}
